import axiosInstance from '../axios/axiosInstance';
import type { PaginatedResponse } from './types';

export interface User {
  _id: string;
  email: string;
  name?: string;
  last_name?: string;
  status: 'active' | 'deactivated' | 'pending';
  createdAt: string;
  updatedAt: string;
  organizationCount?: number;
  workspaceCount?: number;
  projectCount?: number;
  email_verified: boolean;
  deletedAt?: string;
}

export interface UserOrganization {
  _id: string;
  name: string;
  role: string;
  joinedAt: string;
  subscribed: boolean;
}

export interface UserWorkspace {
  _id: string;
  name: string;
  organizationId: string;
  organizationName: string;
  role: string;
  joinedAt: string;
}

export interface UserActivity {
  _id: string;
  action: string;
  resource: string;
  resourceId: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface UserFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'deactivated' | 'pending' | 'all';
  includeDeleted?: boolean;
  emailVerified?: boolean;
  dateRange?: 'all' | '7d' | '30d' | '90d';
  sortBy?: 'name' | 'email' | 'createdAt' | 'organizationCount';
  sortOrder?: 'asc' | 'desc';
}

export interface ActivityFilters {
  page?: number;
  limit?: number;
  dateRange?: 'all' | '7d' | '30d' | '90d';
}

export interface UpdateUserStatusPayload {
  status: 'active' | 'deactivated';
}

export interface TwoFactorStatus {
  userId: string;
  email: string;
  twoFactorEnabled: boolean;
  twoFactorVerified: boolean;
  hasBackupCodes: boolean;
}

export const usersApi = {
  /**
   * Get all users with filters and pagination
   */
  getAll: async (params: UserFilters): Promise<PaginatedResponse<User>> => {
    const { data } = await axiosInstance.get<PaginatedResponse<User>>('/users', { params });
    return data;
  },

  /**
   * Get a single user by ID
   */
  getById: async (id: string): Promise<User> => {
    const { data } = await axiosInstance.get<User>(`/users/${id}`);
    return data;
  },

  /**
   * Update user status (activate/deactivate)
   */
  updateStatus: async (id: string, payload: UpdateUserStatusPayload): Promise<User> => {
    const { data } = await axiosInstance.patch<User>(`/users/${id}/status`, payload);
    return data;
  },

  /**
   * Restore a soft-deleted user
   */
  restore: async (id: string): Promise<User> => {
    const { data } = await axiosInstance.patch<User>(`/users/${id}/restore`);
    return data;
  },

  /**
   * Get all organizations a user belongs to
   */
  getOrganizations: async (
    id: string,
    params?: { page?: number; limit?: number }
  ): Promise<PaginatedResponse<UserOrganization>> => {
    const { data } = await axiosInstance.get<PaginatedResponse<UserOrganization>>(
      `/users/${id}/organizations`,
      { params }
    );
    return data;
  },

  /**
   * Get all workspaces a user has access to
   */
  getWorkspaces: async (
    id: string,
    params?: { page?: number; limit?: number }
  ): Promise<PaginatedResponse<UserWorkspace>> => {
    const { data } = await axiosInstance.get<PaginatedResponse<UserWorkspace>>(
      `/users/${id}/workspaces`,
      { params }
    );
    return data;
  },

  /**
   * Get user activity logs
   */
  getActivity: async (
    id: string,
    params?: ActivityFilters
  ): Promise<PaginatedResponse<UserActivity>> => {
    const { data } = await axiosInstance.get<PaginatedResponse<UserActivity>>(
      `/users/${id}/activity`,
      { params }
    );
    return data;
  },

  /**
   * Send password reset email to user
   */
  sendPasswordReset: async (id: string): Promise<void> => {
    await axiosInstance.post(`/users/${id}/trigger-password-reset`);
  },

  /**
   * Verify user's email manually
   */
  verifyEmail: async (id: string): Promise<User> => {
    const { data } = await axiosInstance.patch<User>(`/users/${id}/verify-email`);
    return data;
  },

  /**
   * Get 2FA status for a user
   */
  getTwoFactorStatus: async (id: string): Promise<TwoFactorStatus> => {
    const { data } = await axiosInstance.get<TwoFactorStatus>(`/users/${id}/two-factor-status`);
    return data;
  },

  /**
   * Reset 2FA for a user
   */
  resetTwoFactor: async (id: string): Promise<{ message: string }> => {
    const { data } = await axiosInstance.post(`/users/${id}/reset-two-factor`);
    return data;
  },

  /**
   * Update user's email address
   */
  updateEmail: async (
    id: string,
    email: string,
    sendConfirmation?: boolean
  ): Promise<{
    message: string;
    oldEmail: string;
    newEmail: string;
    verificationRequired: boolean;
  }> => {
    const { data } = await axiosInstance.patch(`/users/${id}/email`, {
      email,
      sendConfirmation,
    });
    return data;
  },
};
